// src/modules/tenant-improvements/utils/transform-project.js

import { formatDateForLocale } from './dates'

// Defensive against a malformed payload that ships a sparse / null-padded
// `milestones` array (we observed this on at least one tenant). Filtering
// out the nulls at this source keeps every downstream consumer
// (completed count, overdue count, flattenMilestones) free of redundant
// null-checks.
const getAllMilestones = (phases) =>
  phases.reduce(
    (all, phase) => all.concat((phase.milestones || []).filter(Boolean)),
    [],
  )

// Flat shape consumed by the Milestones tab. Phase context is preserved
// via `phaseName` so a future grouped/phase-headered list can use it
// without re-fetching the raw response. `id` is coerced to a string for
// safe use as a React key (the API returns numeric ids today, but
// numeric/string mixing across phases would cause silent key collisions).
const flattenMilestones = (phases) => {
  const list = []
  for (const phase of phases) {
    if (!phase || !Array.isArray(phase.milestones)) continue
    const phaseName = phase.phase_name || ''
    for (const milestone of phase.milestones) {
      if (!milestone) continue
      list.push({
        id: milestone.id != null ? String(milestone.id) : '',
        name: milestone.milestone_name || '',
        dueDate: milestone.due_date || '',
        isCompleted: Boolean(milestone.is_completed),
        phaseName,
      })
    }
  }
  return list
}

// Completion status is intentionally ignored: the card surfaces every
// past-due milestone so users can confirm closeout, even after work is
// marked complete. `now` is hoisted by the caller so every milestone in a
// project is compared against the same instant (and we avoid N redundant
// `new Date()` allocations per project).
const isOverdue = (milestone, now) => {
  if (!milestone.due_date) return false
  const date = new Date(milestone.due_date)
  if (Number.isNaN(date.getTime())) return false
  return date < now
}

export const transformProject = (rawProject) => {
  const now = new Date()
  const phases = rawProject.phases || []
  const milestones = getAllMilestones(phases)
  const completedCount = milestones.filter((m) => m.is_completed).length
  const totalCount = milestones.length
  const progress =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const overdueCount = milestones.filter((m) => isOverdue(m, now)).length

  return {
    id: String(rawProject.id),
    name: rawProject.project_name || '',
    status: rawProject.status || 'Unknown',
    targetDate: formatDateForLocale(rawProject.due_date),
    dueDate: rawProject.due_date || '',
    progress,
    milestones: { completed: completedCount, total: totalCount },
    milestonesList: flattenMilestones(phases),
    alerts: { overdue: overdueCount },
    hasNotification: overdueCount > 0,
    totalAllowance: Number(rawProject.tenant_allowance) || 0,
    description: rawProject.description || '',
  }
}
